/**
 * The AI assistant's history for the company's administrators (map #5, owner 2026-09-24): what was
 * asked, what it answered, what it proposed and what was decided, and problem reports, newest
 * first. From the audit log, which "Ny samtale" and a new day do not clear.
 */
import { get } from '@/api/client';
import { T } from '@/i18n';
import { Empty, LoadError, Panel, Skeleton } from '@/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

export interface HistoryEvent {
  at: string;
  kind: string;
  what: string;
  who: string | null;
  text: string | null;
  proposal: string | null;
  decision: string | null;
}

const when = (iso: string) =>
  new Intl.DateTimeFormat('nb-NO', {
    day: 'numeric',
    month: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Oslo',
  }).format(new Date(iso));

export default function AiHistorikk() {
  const [days, setDays] = useState(30);
  const q = useQuery({
    queryKey: ['poc-history', days],
    queryFn: () =>
      get<{ days: number; truncated?: boolean; events: HistoryEvent[] }>(`/poc/history?days=${days}`),
  });
  const events = q.data?.events ?? [];
  return (
    <Panel
      title={T('AI-assistent: historikk')}
      sub={T('Hva som ble spurt, svart, foreslått og avgjort. «Ny samtale» sletter ikke dette.')}
      actions={
        <select
          className="inp"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          aria-label={T('Periode')}
        >
          <option value={7}>{T('Siste 7 dager')}</option>
          <option value={30}>{T('Siste 30 dager')}</option>
          <option value={90}>{T('Siste 90 dager')}</option>
        </select>
      }
    >
      {q.isPending ? (
        <Skeleton rows={4} />
      ) : q.isError ? (
        <LoadError what={T('historikken')} onRetry={() => q.refetch()} />
      ) : !events.length ? (
        <Empty title={T('Ingenting i perioden')} />
      ) : (
        <>
          {q.data?.truncated && (
            <p className="hint" data-testid="ai-history-truncated">
              {T('Viser de siste 500 hendelsene. Velg en kortere periode for å se alt.')}
            </p>
          )}
          <table className="t" data-testid="ai-history">
            <thead>
              <tr>
                <th>{T('Tid')}</th>
                <th>{T('Hvem')}</th>
                <th>{T('Hva')}</th>
                <th>{T('Innhold')}</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e, i) => (
                <tr key={`${e.at}-${e.kind}-${i}`} className={`aih ${e.kind}`}>
                  <td className="hm">{when(e.at)}</td>
                  <td>{e.who ?? ''}</td>
                  <td>
                    {e.what}
                    {e.decision ? `: ${e.decision}` : ''}
                  </td>
                  <td className="aitext">{e.text ?? e.proposal ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Panel>
  );
}
